import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchRecentReviews } from '../../store/slices/reviewsSlice';
import { FaStar, FaUser } from 'react-icons/fa';

const HomeReviews = () => {
  const dispatch = useDispatch();
  const { recentReviews, loading } = useSelector((state) => state.reviews);

  useEffect(() => {
    dispatch(fetchRecentReviews({ limit: 6 }));
  }, [dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`text-sm ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl font-black text-black mb-6 uppercase tracking-tight">
              Customer Reviews
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-4" />
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
                <div className="h-20 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!recentReviews || recentReviews.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-heading text-5xl md:text-6xl font-black text-black mb-6 uppercase tracking-tight">
            What Our Customers Say
          </h2>
          <p className="text-gray-700 text-xl font-semibold mb-4">
            Real Reviews from Real Customers
          </p>
          <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {recentReviews.map((review) => (
            <div
              key={review._id}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-2xl transition-all transform hover:-translate-y-2"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white">
                    <FaUser />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">{review.userName}</h4>
                    <p className="text-sm text-gray-500">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                {renderStars(review.rating)}
              </div>

              <p className="text-gray-700 leading-relaxed mb-4 line-clamp-4">
                {review.comment}
              </p>

              {review.product && (
                <Link
                  to={`/products/${review.product._id || review.product}`}
                  className="text-primary hover:text-primary-dark font-semibold text-sm flex items-center gap-1"
                >
                  View Product →
                </Link>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/products"
            className="bg-primary text-white px-12 py-4 rounded-lg hover:bg-primary-dark transition-all transform hover:scale-105 shadow-xl font-bold text-lg uppercase tracking-wider inline-block"
          >
            Explore All Products
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeReviews;

